/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable wc/guard-super-call */
import { createElement } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import CodeMirror, { EditorState, EditorView } from '@uiw/react-codemirror';
import * as allThemes from '@uiw/codemirror-themes-all';
import type { Extension } from '@codemirror/state';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import { linter, lintGutter } from '@codemirror/lint';

import { BaseElementWithGlobalStyles, customElement } from '../../base-element';
import { watch } from '../../base-element/directives/watch.directive';

import styles from './code-editor.scss?inline';

export type { Extension };

/**
 * PSTUDIO-1433: map a `language` hint to CodeMirror extensions. Kept as a
 * pure helper so the WC's language→extensions contract is unit-testable
 * without mounting CodeMirror. JSON gets syntax highlighting + a NON-blocking
 * parse linter (squiggles only; the app still commits invalid text).
 */
export function resolveLanguageExtensions(language?: string): Extension[] {
  if (language === 'json') {
    return [json(), linter(jsonParseLinter()), lintGutter()];
  }
  return [];
}

/**
 * PSTUDIO-4340: opt-in soft wrapping of long lines. Off unless `lineWrap` is
 * `true`, so existing consumers keep their horizontally-scrolling editor.
 * CodeMirror's own `.cm-lineWrapping` theme supplies the wrapping CSS.
 */
export function resolveLineWrapExtensions(lineWrap?: boolean): Extension[] {
  return lineWrap === true ? [EditorView.lineWrapping] : [];
}

// themes' list: https://uiwjs.github.io/react-codemirror/#/theme/home
export type ThemeNames =
  | 'abcdef'
  | 'abyss'
  | 'androidstudio'
  | 'andromeda'
  | 'atomone'
  | 'aura'
  | 'basicDark'
  | 'basicLight'
  | 'bbedit'
  | 'bespin'
  | 'consoleDark'
  | 'consoleLight'
  | 'copilot'
  | 'darcula'
  | 'dracula'
  | 'duotoneDark'
  | 'duotoneLight'
  | 'eclipse'
  | 'githubDark'
  | 'githubLight'
  | 'gruvboxDark'
  | 'gruvboxLight'
  | 'kimbie'
  | 'material'
  | 'materialDark'
  | 'materialLight'
  | 'monokai'
  | 'monokaiDimmed'
  | 'noctisLilac'
  | 'nord'
  | 'okaidia'
  | 'quietlight'
  | 'red'
  | 'solarizedDark'
  | 'solarizedLight'
  | 'sublime'
  | 'tokyoNight'
  | 'tokyoNightDay'
  | 'tokyoNightStorm'
  | 'tomorrowNightBlue'
  | 'vscodeDark'
  | 'vscodeLight'
  | 'whiteDark'
  | 'whiteLight'
  | 'xcodeDark'
  | 'xcodeLight';

export const codeEditorThemeList = Object.entries(allThemes)
  .filter(([_key, value]) => typeof value !== 'function')
  .map(([key]) => key)
  .filter((key) => !key.includes('Style'))
  .filter((key) => !key.includes('Init'))
  .filter((key) => !key.includes('defaultSettings')) as ThemeNames[];

@customElement('ps-code-editor')
export class CodeEditorWC extends BaseElementWithGlobalStyles {
  static styles = unsafeCSS(styles);

  root: Root;

  private editorTheme: Extension;

  @property({ type: String }) theme?: ThemeNames = 'githubDark';

  @watch('theme')
  handleThemeChange() {
    // @ts-expect-error
    this.editorTheme = allThemes[this.theme];
  }

  @property() height?: string;

  @property({ type: Array }) extensions?: Extension[];

  @property() value?: string;

  @property({ type: Object }) onChange?: (value: string) => void;

  @property() name?: string;

  @property() language?: 'json';

  /** Soft-wrap long lines instead of scrolling horizontally. Off by default. */
  @property({ type: Boolean }) lineWrap?: boolean;

  // Cached so `render()` reuses ONE stable array reference across re-renders.
  // render() runs on every @property change — including `value` on every
  // keystroke — and @uiw/react-codemirror compares its `extensions` prop by
  // reference (Object.is): a fresh array each render would re-run its
  // StateEffect.reconfigure effect per keystroke (lint flicker + wasted work).
  // Rebuilt ONLY when `language`, `lineWrap` or `extensions` actually change,
  // so a value-only re-render keeps the same reference (mirrors the
  // `editorTheme` @watch pattern).
  private mergedExtensions: Extension[] = [];

  @watch(['language', 'lineWrap', 'extensions'])
  rebuildExtensions() {
    this.mergedExtensions = [
      ...resolveLanguageExtensions(this.language),
      ...resolveLineWrapExtensions(this.lineWrap),
      ...(this.extensions || []),
    ];
  }

  @property({ type: Boolean }) disabled?: boolean;

  private hiddenInput?: HTMLInputElement;

  connectedCallback() {
    super.connectedCallback();
    if (!this.root) {
      this.root = createRoot(this.renderRoot);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Remove the hidden input
    if (this.hiddenInput) {
      this.hiddenInput.remove();
    }
  }

  private updateHiddenInput(value: string) {
    if (this.name && !this.hiddenInput) {
      this.hiddenInput = document.createElement('input');
      this.hiddenInput.type = 'hidden';
      this.hiddenInput.name = this.name;
      this.hiddenInput.value = this.value || '';
      this.appendChild(this.hiddenInput);
    }
    if (this.hiddenInput) {
      this.hiddenInput.value = value;
    }
  }

  public getValue() {
    return this.value;
  }

  render() {
    this.root.render(
      createElement(CodeMirror, {
        height: this.height,
        extensions: this.mergedExtensions,
        value: this.value,
        onCreateEditor: (view: EditorView, state: EditorState) => {
          if (this.value) {
            this.updateHiddenInput(this.value); // Update hidden input value
          }
        },
        onChange: (value: string) => {
          this.value = value;
          this.updateHiddenInput(value); // Update hidden input value
          if (this.onChange) {
            this.onChange(value);
          }
        },
        theme: this.editorTheme,
        editable: !this.disabled,
        basicSetup: {
          highlightActiveLine: !this.disabled,
          highlightActiveLineGutter: !this.disabled,
        },
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-code-editor': CodeEditorWC;
  }
  enum PSElementTagNameMap {
    'ps-code-editor' = 'ps-code-editor',
  }
}
